/**
 * Date display helpers.
 *
 * The rule: a **date-only** value (`"2026-09-09"`, what a Postgres `date`
 * column serializes to) must never round-trip through `Date.parse` /
 * `new Date(...)` for display. The ES spec parses the date-only ISO form as
 * UTC midnight, and `getDate()` reads it back in local time — so every US
 * timezone renders the *previous* day. That is what made a contact date
 * picked as 09/09/2026 come back out of the grid as 09/08/2026 even though
 * the stored value was correct.
 *
 * `wig-react-library` 1.0.116+ exports the same pair (`formatDateOnly` /
 * `formatTimestamp`) from wig-react-library#165, and re-exporting those
 * instead of keeping this copy was the obvious follow-up — but the library
 * ships a single barrel with no deep-import path, so importing it here drags
 * antd into a pure-utility module. Under vitest that is fatal today: antd
 * 6's ESM build uses extensionless imports Node's resolver rejects, so every
 * test touching this file dies at import (the same failure that already
 * takes out `dealsList.test.jsx`). Ten lines duplicated beats coupling the
 * app's date formatting to a UI barrel, so this stays a second
 * implementation deliberately. Keep it byte-equivalent to the library's.
 */

/** Leading `YYYY-MM-DD` of an ISO date or date-time string. */
const ISO_DATE_PARTS = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * Format a date-only value as `mm/dd/yyyy` with no timezone shift.
 *
 * @param {unknown} value Raw API value — `"2026-09-09"`, an ISO date-time
 *   string, a `Date`, `null`, or `undefined`.
 * @returns {string | null} `mm/dd/yyyy`, or `null` for blank input. Values
 *   that are neither ISO-shaped nor parseable come back stringified.
 */
export const formatDateOnly = (value) => {
  if (value == null || value === "") return null;
  const s = String(value);
  const iso = ISO_DATE_PARTS.exec(s);
  // Take the calendar parts verbatim. A Date-typed column carries no
  // meaningful time, so ignoring the time half is exactly right and avoids
  // the UTC-midnight rollback.
  if (iso) return `${iso[2]}/${iso[3]}/${iso[1]}`;
  const t = Date.parse(s);
  if (!Number.isFinite(t)) return s;
  const d = new Date(t);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${d.getFullYear()}`;
};

/**
 * Same as {@link formatDateOnly} but with a 2-digit year (`mm/dd/yy`), for
 * the space-constrained Kanban card chips.
 *
 * @param {unknown} value Raw API value.
 * @returns {string | null} `mm/dd/yy`, or `null` for blank input.
 */
export const formatDateOnlyShort = (value) => {
  const full = formatDateOnly(value);
  if (full == null) return null;
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(full);
  return m ? `${m[1]}/${m[2]}/${m[3].slice(-2)}` : full;
};

/**
 * Format a true instant in the viewer's local timezone. Unlike
 * {@link formatDateOnly}, converting here is correct: the value identifies a
 * moment, not a calendar day.
 *
 * @param {unknown} value Raw API value — ISO date-time string, `Date`,
 *   `null`, or `undefined`.
 * @returns {string | null} Locale date-time string, or `null` when blank.
 */
export const formatTimestamp = (value) => {
  if (value == null || value === "") return null;
  const s = String(value);
  const t = Date.parse(s);
  if (!Number.isFinite(t)) return s;
  return new Date(t).toLocaleString();
};

/**
 * Parse an API date or timestamp to epoch milliseconds, treating a date-only
 * value as **local** midnight rather than the UTC midnight `Date.parse`
 * would give it.
 *
 * Use this for day arithmetic (`daysUntil`, "is it past due", KPI windows),
 * which compares against a local-midnight `today`. Mixing a UTC-parsed
 * date-only value into that comparison leaves it short by the zone offset,
 * which reads as a whole day once floored — a lease expiring today counted
 * as expiring yesterday.
 *
 * @param {unknown} value Raw API value — `"2026-09-09"`, an ISO date-time
 *   string, `null`, or `undefined`.
 * @returns {number | null} Epoch ms, or `null` when blank or unparseable.
 */
export const parseDateMs = (value) => {
  if (value == null || value === "") return null;
  const s = String(value);
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (dateOnly) {
    const [, y, m, d] = dateOnly;
    return new Date(Number(y), Number(m) - 1, Number(d)).getTime();
  }
  const t = Date.parse(s);
  return Number.isFinite(t) ? t : null;
};
